import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { useQuery } from '@apollo/client';
import { ExceptionTypeOptionsProps, SELECT_EMPTY_VALUE } from './config';
import { GET_RESOLVER_CONFIG } from '../../../services/receive-exception/queries';
import i18n from '../../../i18n';

const APP_CONFIG_ERROR_MESSAGE = i18n.t('Error retrieving config');

/**
 * Props object storing the processed ReceiveException AppConfig results, as well as information as to
 * whether the call to retrieve the AppConfig results is still loading, and if the retrieval encountered
 * an error
 */
export type ReceiveExceptionAppConfigProps = {
  resolverGroupOptionDefinitions: Map<string, OptionDefinition[]>;
  fetchConfigError?: string;
  exceptionTypes: ExceptionTypeOptionsProps[];
  loadingResolverConfig: boolean;
};

/**
 * Helper function to remove the beginning "[" and end "]" brackets. Trims the result
 * before and after removing the brackets.
 * @param input string to remove the beginning "[" and end "]" brackets from
 * @returns The input string with the beginning "[" and end "]" brackets. Trims the result
 *          before and after removing the brackets.
 */
function replaceBraketNotation(input: string) {
  return input.trim().replace('[', '').replace(']', '').trim();
}

/**
 * Helper function to convert the passed, parsed AppConfig into list of ExceptionTypeOptionsProps. Filters out
 * disabled entries based off the shouldEnableAllEntries parameter. Prepends the SELECT_EMPTY_VALUE to the
 * beginning of the returned list.
 * @param appConfig Parsed AppConfig
 * @param shouldEnableAllEntries true if all exception types from the AppConfig should be returned. False
 *                               if disabled exception types should be excluded from the results.
 * @returns The passed, parsed AppConfig as a list of ExceptionTypeOptionsProps, where disabled entries are
 *          filtered out based off the shouldEnableAllEntries parameter. Prepends the SELECT_EMPTY_VALUE to the
 *          beginning of the returned list.
 */
function parseAppConfigToFilteredExceptionTypeOptionsProps(
  appConfig: ExceptionTypeToResolverGroupConfiguration[],
  shouldEnableAllEntries: boolean
): ExceptionTypeOptionsProps[] {
  const appConfigExceptionTypeOptionsProps: ExceptionTypeOptionsProps[] = appConfig
    .map((configuration) => {
      return {
        label: i18n.t(configuration.humanFriendlyName),
        value: configuration.exceptionType,
        disabled: !(shouldEnableAllEntries || configuration.exceptionTypeEnabledFlag),
      } as ExceptionTypeOptionsProps;
    })
    .filter((exceptionTypeOptionsProp) => {
      return !exceptionTypeOptionsProp.disabled;
    });
  return [SELECT_EMPTY_VALUE, ...appConfigExceptionTypeOptionsProps];
}

/**
 * Helper function to convert the passed, parsed AppConfig into a map where each key is the exception
 * type value (not the human-friendly name), and where each value is a list of resolver groups in the
 * form of OptionDefinition's
 * @param appConfig Parsed AppConfig
 * @returns a map where each key is the exception type value (not the human-friendly name), and where
 *          each value is a list of resolver groups in the form of OptionDefinition's
 */
function parseAppConfigToOptionDefinitions(
  appConfig: ExceptionTypeToResolverGroupConfiguration[]
): Map<string, OptionDefinition[]> {
  // Retrieve intermediate state
  const exceptionTypeMap: Map<string, OptionDefinition[]> = new Map();
  appConfig.forEach((element) => {
    // Get exception type and list of resolver groups
    const resolverGroups: OptionDefinition[] = element.resolverGroups.map((resolverGroup) => {
      return { label: resolverGroup, value: resolverGroup } as OptionDefinition;
    });
    exceptionTypeMap.set(element.exceptionType, resolverGroups);
  });
  return exceptionTypeMap;
}

/**
 * Helper function to parse the AppConfig string into a representation that provides for
 * easy access to the necessary attributes of each entry
 * @param appConfig AppConfig string
 * @returns ExceptionTypeToResolverGroupConfiguration representation of the AppConfig
 */
function parseAppConfig(appConfig: string): ExceptionTypeToResolverGroupConfiguration[] {
  // Parse appconfig
  const appConfigEntryList: string[] = appConfig.split('\n');
  const configurations: ExceptionTypeToResolverGroupConfiguration[] = [];
  // Start at first index to ignore header values
  for (let i = 1; i < appConfigEntryList.length; i += 1) {
    const configEntry: string = appConfigEntryList[i];
    const splitConfigEntry: string[] = configEntry.split(';');

    // Retrieve and process components
    const exceptionTypeName: string = splitConfigEntry[0].trim();
    const shouldShowExceptionType: boolean =
      replaceBraketNotation(splitConfigEntry[1]).toLowerCase() === 'true';
    const friendlyName: string = replaceBraketNotation(splitConfigEntry[2]).trim();
    const groups: string[] = replaceBraketNotation(splitConfigEntry[3])
      .split(',')
      .map((entry) => {
        return entry.trim();
      });
    const configuration: ExceptionTypeToResolverGroupConfiguration = {
      exceptionType: exceptionTypeName,
      exceptionTypeEnabledFlag: shouldShowExceptionType,
      humanFriendlyName: friendlyName,
      resolverGroups: groups,
    };
    configurations.push(configuration);
  }
  return configurations;
}

/**
 * React hook function to call GraphQL to retrieve information regarding exception types and their corresponding
 * resolver groups.
 * The following is an example AppConfig:
 *
 * ```
 * Exception Type; Exception Type Enabled Flag; Human Friendly Name; Resolver Groups
 * INVALID_PO; [true]; [Invalid PO]; [LOGISTICS, VMI TEAM, NW TPMS]
 * INVALID_SERIAL; [true]; [Invalid Serial Number]; [LOGISTICS, VMI TEAM]
 * SYSTEM_ERROR; [false]; [System Error]; [LOGISTICS]
 * ```
 *
 * @param shouldIncludeAllExceptionTypes true if all exception types from the AppConfig should be returned. False
 *                                       if disabled exception types should be excluded from the results.
 * @returns ReceiveExceptionAppConfigProps containing info on whether the GraphQL call is loading,
 *          if there was an error retrieving the AppConfig, and the parsed AppConfig data itself
 */
export function useQueryAppConfig(
  shouldIncludeAllExceptionTypes: boolean
): ReceiveExceptionAppConfigProps {
  // Call GraphQL for AppConfig
  const {
    loading: loadingResolverConfig,
    error: queryError,
    data: appConfigQueryResult,
  } = useQuery(GET_RESOLVER_CONFIG, {});

  // Return early if still loading
  if (loadingResolverConfig) {
    return {
      resolverGroupOptionDefinitions: new Map(),
      exceptionTypes: [],
      loadingResolverConfig,
    };
  }
  if (queryError) {
    return {
      resolverGroupOptionDefinitions: new Map(),
      exceptionTypes: [],
      loadingResolverConfig,
      fetchConfigError: APP_CONFIG_ERROR_MESSAGE,
    };
  }

  // Parse and return AppConfig data
  const parsedAppConfig: ExceptionTypeToResolverGroupConfiguration[] = parseAppConfig(
    appConfigQueryResult.getReceiveExceptionResolverConfig.config
  );
  const exceptionTypes: ExceptionTypeOptionsProps[] = parseAppConfigToFilteredExceptionTypeOptionsProps(
    parsedAppConfig,
    shouldIncludeAllExceptionTypes
  );
  const resolverGroupOptionDefinitions: Map<
    string,
    OptionDefinition[]
  > = parseAppConfigToOptionDefinitions(parsedAppConfig);
  return {
    resolverGroupOptionDefinitions,
    exceptionTypes,
    loadingResolverConfig,
  };
}

/**
 * Intermediate object that allos for easy access to the parts of an AppConfig entry
 */
type ExceptionTypeToResolverGroupConfiguration = {
  exceptionType: string;
  exceptionTypeEnabledFlag: boolean;
  humanFriendlyName: string;
  resolverGroups: string[];
};
